import {Observable, of, ReplaySubject, Subject} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {Injectable} from "@angular/core";
import moment, {Duration, Moment, MomentInput} from 'moment-timezone';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {BaseUserService} from "@shared/services/api/base-user.service";
import {ClassTransformer, Transform, Type} from "class-transformer";
import {BacnetDeviceId, CollectorCommandId, CollectorId} from "@shared/util/app-types";
import {HttpUtilityService} from "@shared/services/api/http-utility-service";
import {AceLoggerService} from "@logging/ace-logger.service";
import {AceLogger} from "@logging/ace-logger";
import {APIResponse} from "@app/layout/admin/services/api/api.interface";

export enum CollectorCommandType {
  DISCOVER_DEVICES = 'discover-devices',
  DISCOVER_DEVICE_BY_ID = 'discover-device-by-id',
  GET_ALL_DEVICE_OBJECT_PROPERTIES = 'get-all-device-object-properties',
  GET_DEVICE_OBJECT_PRESENT_VALUE = 'get-device-object-present-value',
  SYNCHRONISE_COLLECTOR = 'synchronise-collector'
}

export enum TestConnectionCommandType {
  HAYSTACK = 'test-connection-haystack',
  SMARTCONNECTOR = 'test-connection-smartconnector',
  SIEMENS = 'test-connection-siemens',
  SQLSERVER = 'test-connection-sqlserver'
}

export type CollectorCommandTypes = CollectorCommandType | TestConnectionCommandType;

export enum CollectorCommandStatus {
  QUEUED = 'Queued',
  STARTED = 'Started',
  COMPLETED = 'Completed',
  FAILED = 'Failed'
}

class CollectorCommandResponse {
  collector_command: CollectorCommand;
}

class CollectorCommand {
  command_id: CollectorCommandId;
  collector_id: CollectorId;
  command_type: CollectorCommandType;
  command_params: {[key: string]: boolean | number | string};
  @Type(() => moment)
  @Transform(({value}) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  created_at: Moment;
  @Type(() => moment)
  @Transform(({value}) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  last_modified_at: Moment;
  status: CollectorCommandStatus;
  result: {[key: string]: any};
}

class CollectorCommandParams {
  bacnet_device_id?: BacnetDeviceId;
  object_identifier?: string;
}

export enum CollectorCommandResultStatus {
  SUCCESS = 'Success',
  FAILURE = 'Failure'
}

export class CollectorCommandResult {
  status: CollectorCommandResultStatus;
  result: {[key: string]: any};
}

@Injectable({
  providedIn: 'root'
})
export class CollectorCommandService extends BaseUserService {

  private static readonly POLL_INTERVAL = moment.duration(10, 'seconds');
  private static readonly QUEUED_TIMEOUT = moment.duration(5, 'minutes');

  private static readonly DISCOVER_DEVICES_STARTED_TIMEOUT = moment.duration(10, 'minutes');
  private static readonly STANDARD_COMMAND_STARTED_TIMEOUT = moment.duration(2, 'minutes');

  private readonly logger: AceLogger;

  private headers: HttpHeaders = this.getHeaders();

  private static isQueuedTimeout(command: CollectorCommand, now: MomentInput): boolean {
    return command.status === CollectorCommandStatus.QUEUED &&
      CollectorCommandService.isExpired(command, now, CollectorCommandService.QUEUED_TIMEOUT);
  }

  private static isStartedTimeout(command: CollectorCommand, now: MomentInput): boolean {
    if (command.status !== CollectorCommandStatus.STARTED) {
      return false;
    }

    const timeout = command.command_type == CollectorCommandType.DISCOVER_DEVICES ?
      CollectorCommandService.DISCOVER_DEVICES_STARTED_TIMEOUT :
      CollectorCommandService.STANDARD_COMMAND_STARTED_TIMEOUT;

    return CollectorCommandService.isExpired(command, now, timeout);
  }

  private static isExpired(command: CollectorCommand, now: MomentInput, timeout: Duration): boolean {
    return command.last_modified_at.add(timeout).isBefore(now);
  }

  private static makeFailureResult(message: string): CollectorCommandResult {
    return {status: CollectorCommandResultStatus.FAILURE, result: {error: {message: message}}};
  }

  private static publish(subject: Subject<CollectorCommandResult>, result: CollectorCommandResult): void {
    subject.next(result);
    subject.complete();
  }

  constructor(private classTransformer: ClassTransformer,
              private httpUtilityService: HttpUtilityService,
              private http: HttpClient,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(CollectorCommandService.name);
  }

  public executeDiscoverDevices(collectorId: CollectorId): Observable<CollectorCommandResult> {
    return this.executeCommand(collectorId, CollectorCommandType.DISCOVER_DEVICES);
  }

  public executeDiscoverDeviceById(collectorId: CollectorId, bacnetDeviceId: BacnetDeviceId): Observable<CollectorCommandResult> {
    return this.executeCommand(
      collectorId,
      CollectorCommandType.DISCOVER_DEVICE_BY_ID,
      {bacnet_device_id: bacnetDeviceId}
    );
  }

  public executeGetAllDeviceObjectProperties(collectorId: CollectorId, bacnetDeviceId: BacnetDeviceId): Observable<CollectorCommandResult> {
    return this.executeCommand(
      collectorId,
      CollectorCommandType.GET_ALL_DEVICE_OBJECT_PROPERTIES,
      {bacnet_device_id: bacnetDeviceId});
  }

  public executeGetDeviceObjectPresentValue(collectorId: CollectorId, bacnetDeviceId: BacnetDeviceId, objectIdentifier: string): Observable<CollectorCommandResult> {
    return this.executeCommand(
      collectorId,
      CollectorCommandType.GET_DEVICE_OBJECT_PRESENT_VALUE,
      {bacnet_device_id: bacnetDeviceId, object_identifier: objectIdentifier}
    );
  }

  public executeSynchroniseCollector(collectorId: CollectorId): Observable<CollectorCommandResult> {
    return this.executeCommand(collectorId, CollectorCommandType.SYNCHRONISE_COLLECTOR);
  }

  public executeTestConnection(collectorId: CollectorId, commandType: TestConnectionCommandType): Observable<CollectorCommandResult> {
    return this.executeCommand(collectorId, commandType);
  }

  private executeCommand(collectorId: CollectorId,
                         commandType: CollectorCommandTypes,
                         commandParams: CollectorCommandParams = {}): Observable<CollectorCommandResult> {

    return this
      .createCommand(collectorId, commandType, commandParams)
      .pipe(
        tap(command => this.logger.debug(command)),
        map((command: CollectorCommand) => {
          const subject = new ReplaySubject<CollectorCommandResult>(1);
          return {commandId: command.command_id, subject: subject};
        }),
        tap(pair => this.scheduleFetch(pair.commandId, collectorId, pair.subject)),
        mergeMap(pair => pair.subject.asObservable()),
        catchError(error => {
            this.logger.warn("Error fetching collector command: " + error);
            return of(CollectorCommandService.makeFailureResult('Failed to create command on server'));
          }
        ));
  }

  private createCommand(collectorId: CollectorId,
                        commandType: CollectorCommandTypes,
                        commandParams: CollectorCommandParams): Observable<CollectorCommand> {

    const url = this.makeCreateCollectorCommandUrl(collectorId);
    const options = {headers: this.headers};
    const payload = {
      command_type: commandType,
      command_params: commandParams
    };

    return this.http
      .post<APIResponse<CollectorCommandResponse>>(url, payload, options)
      .pipe(map(response => this.classTransformer.plainToInstance(CollectorCommand, response.data.collector_command)));
  }

  private fetchCommand(collectorId: CollectorId, commandId: CollectorCommandId, subject: Subject<CollectorCommandResult>): void {

    if (!subject.observed) {
      this.logger.debug("Cancelling fetch command due to no observers: " + commandId);
      return;
    }

    const url = this.makeFetchCollectorCommandUrl(collectorId, commandId);
    const options = {headers: this.headers};

    this.http.get<APIResponse<CollectorCommandResponse>>(url, options)
      .subscribe({
        next: (response) => {
          const command = this.classTransformer.plainToInstance(CollectorCommand, response.data.collector_command);
          this.logger.debug(command);
          this.handleUpdate(command, subject);
        },
        error: (error) => {
          this.logger.warn("Error fetching collector command: " + commandId + " - " + error);
          const result: CollectorCommandResult = CollectorCommandService.makeFailureResult('Failed to poll command on server');
          CollectorCommandService.publish(subject, result);
        }
      });
  }

  private handleUpdate(command: CollectorCommand, subject: Subject<CollectorCommandResult>) {

    if (command.status === CollectorCommandStatus.COMPLETED) {
      const result: CollectorCommandResult = {status: CollectorCommandResultStatus.SUCCESS, result: command.result};
      CollectorCommandService.publish(subject, result);
      return;
    }

    const now = moment.now();

    if (command.status === CollectorCommandStatus.FAILED) {
      const result: CollectorCommandResult = {status: CollectorCommandResultStatus.FAILURE, result: command.result};
      CollectorCommandService.publish(subject, result);
      return;
    }

    if (CollectorCommandService.isQueuedTimeout(command, now)) {
      const result = CollectorCommandService.makeFailureResult('Queued command timed out');
      CollectorCommandService.publish(subject, result);
      return;
    }

    if (CollectorCommandService.isStartedTimeout(command, now)) {
      const result = CollectorCommandService.makeFailureResult('Started command timed out');
      CollectorCommandService.publish(subject, result);
      return;
    }

    this.scheduleFetch(command.command_id, command.collector_id, subject);
  }

  private scheduleFetch(commandId: CollectorCommandId,
                        collectorId: CollectorId,
                        subject: Subject<CollectorCommandResult>): void {

    setTimeout(() => {
      this.fetchCommand(collectorId, commandId, subject);
    }, CollectorCommandService.POLL_INTERVAL.as('milliseconds'));
  }

  private makeCreateCollectorCommandUrl(collectorId: CollectorId): string {
    return this.getGateway() + '/collectors/' + encodeURIComponent(collectorId) + '/commands';
  }

  private makeFetchCollectorCommandUrl(collectorId: CollectorId, commandId: CollectorCommandId): string {
    return this.makeCreateCollectorCommandUrl(collectorId) + '/' + encodeURIComponent(commandId);
  }
}
